import {
  Box,
  Chip,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { ColDef, GridOptions, ICellRendererParams } from "ag-grid-community";
import * as echarts from "echarts";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import notVerifiedImg from "../../../assets/baseVersion/False_img.svg";
import verifiedImg from "../../../assets/baseVersion/True_img.svg";
import axiosInstance from "../../../axios/axios";
import AgGridTable from "../../../components/table/AgGridTable";
// import Tooltip from "../../../components/Tooltip";
import { END_POINTS } from "../../../shared";
import {
  FRAMEWORK_NOT_ENABLED_MESSAGE,
  isFrameworkNotEnabledError,
} from "../../../shared/hooks/useFrameworks";
import SkeletonLoader from "../../../modules/dashboard/shared/skeleton_loader";
import "react-loading-skeleton/dist/skeleton.css";
import WhiteTooltip from "../../../components/WhiteTooltip";

const CveComplience = ({ assetDetails }: any) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [complianceData, setComplianceData] = useState<any>([]);
  const [selectedControlType, setSelectedControlType] = useState<string>("");
  const [selectedData, setSelectedData] = useState<any>(null);
  const [complianceParameterData, setComplianceParameterData] = useState<any>(
    []
  );
  const [complianceGraphData, setComplianceGraphData] = useState<any>([]);
  const timelineChartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    suppressMovable: false,
  };
  const [isComponentMounted, setIsComponentMounted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isGraphLoading, setIsGraphLoading] = useState(false);
  // Set when the backend refuses the selected framework (switched off for the organization)
  const [frameworkDisabled, setFrameworkDisabled] = useState(false);

  const gridOptions: GridOptions = {
    defaultColDef: defaultColDef,
    rowHeight: 48,
    headerHeight: 48,
    suppressCellFocus: true,
    rowStyle: { cursor: "default" },
    suppressRowClickSelection: true,
    rowMultiSelectWithClick: false,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    rowSelection: "multiple" as const,
    onSelectionChanged: (event: any) => {
      const selectedNodes = event.api.getSelectedNodes();
      const selectedData = selectedNodes.map((node: any) => node.data);
    },
  };

  const complianceDetails = async (deviceId: string) => {
    try {
      setIsLoading(true);
      setFrameworkDisabled(false);
      const response = await axiosInstance.get(END_POINTS.ASSET_COMPLIANCE, {
        params: { device_id: deviceId },
      });

      if (response.status === 200) {
        const data = response?.data?.data?.compliance_summary;
        setComplianceData(data);

        // Set initial selected control type and data
        if (data && data.length > 0) {
          setSelectedControlType(data[0]?.control_type);
          setSelectedData(data[0]);
          // Call these functions without setting loading state here since they're part of initial load
          await complianceParameter(data[0].id);
          await getComplianceGraphData(data[0].control_type);
        }
      }
    } catch (error: any) {
      console.error("Error fetching agent list:", error.response);
      if (isFrameworkNotEnabledError(error)) {
        setFrameworkDisabled(true);
      } else {
        toast.error("We couldn't load this asset's compliance. Please try again in a moment.", {
          type: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const complianceParameter = async (controlType: any) => {
    try {
      setIsTableLoading(true);
      const response = await axiosInstance.get(
        END_POINTS.ASSET_COMPLIANCE_DETAILS,
        {
          params: { device_id: assetDetails?.id, control_type_id: controlType },
        }
      );

      if (response.status === 200) {
        const data = response?.data?.data;

        // Set initial selected control type and data
        if (data) {
          setComplianceParameterData(data?.controls);
        }
      }
    } catch (error: any) {
      console.error("Error fetching agent list:", error.response);
      if (isFrameworkNotEnabledError(error)) {
        setComplianceParameterData([]);
        setFrameworkDisabled(true);
      } else {
        toast.error("We couldn't load the compliance controls. Please try again in a moment.", {
          type: "error",
        });
      }
    } finally {
      setIsTableLoading(false);
    }
  };

  const getComplianceGraphData = async (controlType: any) => {
    try {
      setIsGraphLoading(true);
      const currentDate = new Date();
      const endDate = currentDate.toISOString().split("T")[0]; // Today's date in YYYY-MM-DD format
      const startDate = new Date(
        currentDate.setDate(currentDate.getDate() - 30)
      )
        .toISOString()
        .split("T")[0]; // Last 30 days
      let payload: any = {
        device_id: assetDetails?.id,
        duration: "custom_date_range",
        start_date: startDate,
        end_date: endDate,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      // Assigning the control type directly to the payload
      payload.is_compliance = true;
      payload.framework_data = controlType;

      const response = await axiosInstance.post(
        END_POINTS.DASHBOARD_COMPLIANCE,
        payload
      );
      if (response.status === 200) {
        const data = response?.data?.data;
        setComplianceGraphData(data?.graph_data);
      }
    } catch (error: any) {
      console.error("Error fetching compliance graph data:", error.response);
      if (isFrameworkNotEnabledError(error)) {
        setComplianceGraphData([]);
        setFrameworkDisabled(true);
      } else {
        toast.error("We couldn't load the compliance trend. Please try again in a moment.", {
          type: "error",
        });
      }
    } finally {
      setIsGraphLoading(false);
    }
  };

  const initChart = useCallback(() => {
    console.log("Initializing chart...", {
      element: timelineChartRef.current,
      chartInstance: chartInstance.current,
    });

    if (!timelineChartRef.current) {
      return;
    }

    // Dispose of existing instance if it exists
    if (chartInstance.current) {
      chartInstance.current.dispose();
    }

    try {
      chartInstance.current = echarts.init(timelineChartRef.current);

      const isCIS =
        selectedControlType === "CIS" || selectedControlType.startsWith("CIS_");
      const series = isCIS
        ? [
          {
            name: "Implementation Group 1",
            type: "line",
            smooth: true,
            symbol: "circle",
            symbolSize: 6,
            data: complianceGraphData.map(
              (item: any) => Number(parseFloat(item["Implementation Group 1"]).toFixed(2))
            ),
            itemStyle: {
              color: "#4F46E5",
            },
            lineStyle: {
              width: 3,
              color: "#4F46E5",
            },
          },
          {
            name: "Implementation Group 2",
            type: "line",
            smooth: true,
            symbol: "circle",
            symbolSize: 6,
            data: complianceGraphData.map(
              (item: any) => Number(parseFloat(item["Implementation Group 2"]).toFixed(2))
            ),
            itemStyle: {
              color: "#10B981",
            },
            lineStyle: {
              width: 3,
              color: "#10B981",
            },
          },
          {
            name: "Implementation Group 3",
            type: "line",
            smooth: true,
            symbol: "circle",
            symbolSize: 6,
            data: complianceGraphData.map(
              (item: any) => Number(parseFloat(item["Implementation Group 3"]).toFixed(2))
            ),
            itemStyle: {
              color: "#F59E0B",
            },
            lineStyle: {
              width: 3,
              color: "#F59E0B",
            },
          },
        ]
        : [
          {
            name: "Compliance",
            type: "line",
            smooth: true,
            symbol: "circle",
            symbolSize: 6,
            data: complianceGraphData.map((item: any) =>
              item[selectedControlType] !== undefined &&
                item[selectedControlType] !== null
                ? Number(item[selectedControlType]).toFixed(2)
                : null
            ),
            itemStyle: {
              color: "#4F46E5",
            },
            lineStyle: {
              width: 3,
              color: "#4F46E5",
            },
            areaStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  {
                    offset: 0,
                    color: "rgba(79, 70, 229, 0.2)",
                  },
                  {
                    offset: 1,
                    color: "rgba(79, 70, 229, 0)",
                  },
                ],
              },
            },
          },
        ];

      // Check if series data is empty
      const hasData = series.some((s: any) => s.data && s.data.length > 0);

      const timelineOption = {
        tooltip: {
          trigger: "axis",
        },
        legend: {
          data: isCIS
            ? [
              "Implementation Group 1",
              "Implementation Group 2",
              "Implementation Group 3",
            ]
            : ["Compliance"],
          top: 0,
          right: 0,
          show: hasData, // Hide legend when no data
          textStyle: {
            color: isDarkMode ? "#E2E8F0" : "#374151",
            fontWeight: 600,
          },
        },
        grid: {
          left: "3%",
          right: "5%",
          bottom: "3%",
          top: "40px",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: complianceGraphData.map((item: any) => item.date_time),
          axisLine: {
            lineStyle: {
              color: isDarkMode ? "rgba(255, 255, 255, 0.12)" : "#E5E7EB",
            },
          },
          axisLabel: {
            color: isDarkMode ? "#9CA3AF" : "#6B7280",
          },
          show: hasData, // Hide x-axis when no data
        },
        yAxis: {
          type: "value",
          min: 0,
          max: 100,
          interval: 20,
          axisLine: {
            show: false,
          },
          splitLine: {
            lineStyle: {
              color: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "#E5E7EB",
              type: "dashed",
            },
          },
          axisLabel: {
            color: isDarkMode ? "#9CA3AF" : "#6B7280",
          },
          show: hasData, // Hide y-axis when no data
        },
        series: hasData ? series : [], // Hide series when no data
        graphic: hasData || isLoading || isGraphLoading
          ? " "
          : {
            type: "text",
            left: "center",
            top: "middle",
            style: {
              text: "No data available",
              fontSize: 12,
              fontWeight: 400,
              fill: isDarkMode ? '#9CA3AF' : '#6B7280',
              textAlign: "center",
            },
          },
      };

      chartInstance.current.setOption(timelineOption);
    } catch (error) {
      console.error("Error initializing chart:", error);
    }
  }, [complianceGraphData, selectedControlType]);

  // Handle window resize
  useEffect(() => {
    console.log(assetDetails, "assetDetails");

    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  // Initialize chart after component mount and DOM is ready
  useLayoutEffect(() => {
    initChart();
  }, [initChart]);

  // Reinitialize chart when data changes
  useEffect(() => {
    if (complianceData.length > 0 || complianceGraphData.length > 0 || isLoading || isGraphLoading) {
      initChart();
    }
  }, [complianceData, complianceGraphData, isLoading, isGraphLoading, initChart]);

  // Component mount effect
  useEffect(() => {
    setIsComponentMounted(true);
    return () => setIsComponentMounted(false);
  }, []);

  // Initial data load
  useEffect(() => {
    if (assetDetails?.id) {
      complianceDetails(assetDetails.id);
    }
  }, [assetDetails?.id]);

  // Handle control type change
  const handleControlTypeChange = (value: string) => {
    setSelectedControlType(value);
    const selected = complianceData.find(
      (item: any) => item.control_type === value
    );
    setSelectedData(selected);
    setFrameworkDisabled(false);
    if (selected?.id) {
      complianceParameter(selected.id);
      getComplianceGraphData(selected.control_type);
    }
  };

  // Calculate stroke color based on compliance percentage
  const getStrokeColor = (compliance: number) => {
    if (compliance >= 70) return "#22C55E";
    if (compliance >= 40) return "#FFA500";
    return "#EF4444";
  };
  const ControlIdCellRenderer = (params: any) => {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        //  marginTop={1.5} 
        height="100%"
      >


        <WhiteTooltip
          title={
            <div
              className="scroll"
              style={{
                maxHeight: 200,
                overflowY: "auto",
                whiteSpace: "pre-line",
                paddingRight: 8,
              }}
            >            {params.value}
            </div>
          }
          arrow
          placement="bottom"
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: "#fff",
                color: "#000",
                boxShadow: 1,
                fontSize: "0.775rem",
              },
            },
            arrow: {
              sx: {
                color: "#fff",
              },
            },

          }}



        >
          <Typography
            variant="body2"
            noWrap
            sx={{
              maxWidth: 300,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value}
          </Typography>



        </WhiteTooltip>

      </Box>
    );
  };
  const agGridInlineStyles = useMemo(
    () => ({
      padding: "0px",
      // "& .ag-body-vertical-scroll": {
      //   width: "0px !important",
      //   minWidth: "0px !important",
      // },
      // "& .ag-body-vertical-scroll-viewport": {
      //   width: "0px !important",
      //   minWidth: "0px !important",
      // },
      "& .ag-center-cols-clipper": {
        marginRight: 0,
      },
      "& .ag-center-cols-viewport": {
        marginRight: 0,
      },
    }),
    []
  );
  const columnDefs: ColDef[] = [
    {
      field: "",
      headerName: "#",
      filter: false,
      minWidth: 60,
      maxWidth: 70,
      valueGetter: (params: any) => {
        return params.node?.rowIndex !== undefined
          ? (params.node.rowIndex + 1).toString()
          : "";
      },
      sortable: false,
    },
    {
      field: "control_version",
      headerName: "Control ID",
      filter: true,
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
      minWidth: 130,

      flex: 1,

      cellRenderer: ControlIdCellRenderer,
    },
    {
      field: "name",
      headerName: "Name",
      filter: true,
      minWidth: 250,
      flex: 1,
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
      cellRenderer: ControlIdCellRenderer,

    },
    {
      field: "description",
      headerName: "Description",
      filter: true,
      minWidth: 200,
      maxWidth: 400,
      width: 200,
      flex: 2,
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
      cellRenderer: ControlIdCellRenderer,

    },
    {
      field: "compliance",
      headerName: "Compliance",
      filter: true,
      minWidth: 150,
      flex: 1,
      headerComponent: () => (
        <div style={{ width: "100%", textAlign: "center", fontWeight: 600, fontSize: "12px" }}>
          Compliance
        </div>
      ),
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%" marginTop={1.5}>

            <img
              src={params.value ? verifiedImg : notVerifiedImg}
              alt={params.value ? "Verified" : "Not Verified"}
              style={{ width: "20px", height: "20px" }}
            />
          </Box>
        );
      },
    },
  ];

  if (isLoading) {
    return (

      <SkeletonLoader
        count={Math.floor(700 / 40)}
        styles={{ height: 30 }}
      />

    );
  }

  if (!selectedData) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "600px",
          fontSize: "16px",
          backgroundColor: "#fafafa",
        }}
      >
        {frameworkDisabled ? FRAMEWORK_NOT_ENABLED_MESSAGE : "No compliance data available"}
      </div>
    );
  }

  return (
    <div>
      <Box component="div" sx={{ p: 0, m: 0 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 0 }}>
          {/* Control Type Dropdown */}
          <FormControl
            size="small"
            sx={{ width: "90px", padding: "0px", borderRadius: "1px" }}
          >
            <Select
              value={selectedControlType || ""}
              onChange={(e) => handleControlTypeChange(e.target.value)}
              variant="outlined"
              displayEmpty
            >
              {Array.isArray(complianceData) &&
                complianceData.map((item: any) => (
                  <MenuItem key={item.id} value={item.control_type}>
                    {item.control_type}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Compliance */}
          <Box
            sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontWeight: "600" }}
            >
              Compliance%:
            </Typography>
            <Box sx={{ position: "relative", alignItems: "center" }}>
              <svg width="40" height="40" viewBox="0 0 36 36">
                {(() => {
                  const radius = 10.9155;
                  const circumference = 2 * Math.PI * radius;
                  const compliance = Number(selectedData.compliance);
                  const offset = circumference - (compliance / 100) * circumference;

                  return (
                    <>
                      {/* Background Circle */}
                      <circle
                        cx="18"
                        cy="18"
                        r={radius}
                        fill="none"
                        stroke="#e0e0e0"
                        strokeWidth="5"
                      />
                      {/* Progress Circle */}
                      <circle
                        cx="18"
                        cy="18"
                        r={radius}
                        fill="none"
                        stroke={getStrokeColor(compliance)}
                        strokeWidth="5"
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        // strokeLinecap="round"
                        transform="rotate(-90 18 18)" // Start progress from top
                      />
                    </>
                  );
                })()}
              </svg>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: "600" }}>
              {selectedData.compliance}%
            </Typography>
          </Box>


          <Divider variant="middle" orientation="vertical" flexItem />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              gutterBottom={false}
              sx={{ fontWeight: 550, mr: 1 }}
            >
              Stage:
            </Typography>
            <Chip
              label={selectedData.stage?.substring(0, 3)}
              size="small"
              sx={{
                bgcolor: "#6366F1",
                color: "white",
                fontWeight: "medium",
                borderRadius: "4px",
              }}
            />
          </Box>

          <Divider variant="middle" orientation="vertical" flexItem />

          <Box
            sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
          >
            <Typography
              variant="body2"
              color="#696595"
              sx={{ fontWeight: "600", fontSize: "16px", mr: 1 }}
            >
              Passed:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#00A61B", fontWeight: "600", fontSize: "18px" }}
            >
              {selectedData.passed_count}
            </Typography>
          </Box>

          <Divider variant="middle" orientation="vertical" flexItem />

          {/* Failed */}
          <Box
            sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}
          >
            <Typography
              variant="body2"
              color="#696595"
              sx={{ fontWeight: "600", fontSize: "16px", mr: 1 }}
            >
              Failed:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#F03C3C", fontWeight: "600", fontSize: "18px" }}
            >
              {selectedData.failed_count}
            </Typography>
          </Box>
        </Stack>
        <hr style={{ marginBottom: "10px" }} />

        <Box
          sx={{ pl: 0, width: "100%", height: "200px", position: "relative" }}
        >
          <p
            style={{ marginBottom: "0px", fontWeight: "900", fontSize: "14px" }}
          >
            TRENDS
          </p>
          <div
            ref={timelineChartRef}
            style={{
              width: "100%",
              height: "200px",
              visibility: "visible",
            }}
          />
        </Box>
        <hr style={{ marginTop: "20px" }} />

        <Box sx={{ mt: "10px" }}>
          {complianceParameterData.length === 0 ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "calc(100vh - 497px)",
                fontSize: "16px",
                backgroundColor: "#fafafa",
              }}
            >
              {frameworkDisabled ? FRAMEWORK_NOT_ENABLED_MESSAGE : "No data available"}
            </div>
          ) :
            (
              <AgGridTable
                columnDefs={columnDefs}
                data={complianceParameterData}
                styles={agGridInlineStyles}
                height={"calc(100vh - 497px)"}
                showSearch
                headerActions={
                  <Box display="flex" alignItems="center" gap={2}></Box>
                }
                isRowSelectable={true}
                rowSelection="multiple"
                gridOptions={gridOptions}
                isLoading={isLoading}

              />
            )}

        </Box>
      </Box>
    </div>
  );
};

export default CveComplience;


